import { useState, useEffect, useRef, useCallback } from 'react'
import { fetchApi } from '@/lib/api'
import { createBugFixSocket, type BugFixEvent } from '@/lib/bugfixWs'
import { createCwAlarmSocket, type CwAlarmEvent } from '@/lib/cwAlarmWs'
import { MarkdownRenderer } from '@/components/MarkdownRenderer'
import {
  ShieldAlert, Play, XCircle, History,
  CheckCircle, AlertCircle, Loader2, MinusCircle,
  Clock, ChevronDown, ChevronRight,
  ExternalLink, AlertTriangle, ArrowRight,
  Search, GitPullRequest, ClipboardList,
  Bell,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime } from '@/lib/date'

// ==========================================================================
// Shared Types & Constants
// ==========================================================================

const PIPELINE_STEPS = ['investigate', 'fix', 'push_pr', 'twp_task', 'done'] as const
const STEP_LABELS: Record<string, string> = {
  investigate: '調査',
  fix: '修正',
  push_pr: 'PR',
  twp_task: 'タスク',
  done: '完了',
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-gray-500/20 text-gray-400' },
  running: { label: 'Running', cls: 'bg-blue-500/20 text-blue-400' },
  completed: { label: 'Completed', cls: 'bg-green-500/20 text-green-400' },
  failed: { label: 'Failed', cls: 'bg-red-500/20 text-red-400' },
  cancelled: { label: 'Cancelled', cls: 'bg-yellow-500/20 text-yellow-400' },
  interrupted: { label: 'Interrupted', cls: 'bg-orange-500/20 text-orange-400' },
}

// --- Shared Helpers ---

function SessionStatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.pending
  return <span className={cn('px-2 py-0.5 rounded text-xs font-medium', s.cls)}>{s.label}</span>
}

function StepIcon({ status }: { status: string }) {
  switch (status) {
    case 'success':
    case 'pr_created':
    case 'fix_created':
    case 'not_a_bug':
    case 'not_an_issue':
    case 'done':
      return <CheckCircle size={14} className="text-green-400" />
    case 'running':
      return <Loader2 size={14} className="text-blue-400 animate-spin" />
    case 'failed':
      return <AlertCircle size={14} className="text-red-400" />
    case 'escalation':
      return <AlertTriangle size={14} className="text-orange-400" />
    case 'duplicate':
      return <MinusCircle size={14} className="text-cyan-400" />
    case 'skipped':
      return <MinusCircle size={14} className="text-yellow-500/60" />
    default:
      return <Clock size={14} className="text-[var(--color-text-muted)]/40" />
  }
}

// ==========================================================================
// Sentry Bug Fix Types
// ==========================================================================

type SentrySessionSummary = {
  id: number
  status: string
  trigger_type: string
  total_issues: number
  pr_created: number
  not_a_bug: number
  escalated: number
  skipped: number
  failed: number
  duplicate: number
  started_by: string
  error_message: string | null
  created_at: string
  completed_at: string | null
}

type SentryIssueDetail = {
  id: number
  sentry_issue_id: string
  sentry_project: string | null
  sentry_title: string | null
  sentry_level: string | null
  sentry_culprit: string | null
  sentry_permalink: string | null
  sentry_first_seen: string | null
  sentry_last_seen: string | null
  sentry_count: string | null
  step: string
  status: string
  result: string
  repo_name: string | null
  pr_url: string | null
  pr_number: number | null
  pr_branch: string | null
  twp_task_id: string | null
  twp_task_key: string | null
  investigation_summary: string | null
  escalation_detail: string | null
  error_message: string | null
  created_at: string
  updated_at: string
}

type SentrySessionDetail = SentrySessionSummary & {
  issues: SentryIssueDetail[]
}

type SentryTrackerEntry = {
  sentry_issue_id: string
  latest_session_id: number
  latest_status: string
  pr_url: string | null
  twp_task_id: string | null
  twp_task_key: string | null
  repo_name: string | null
  sentry_title: string | null
  sentry_project: string | null
  created_at: string
  updated_at: string
}

type SchedulerStatus = {
  auto_enabled: boolean
  interval_seconds: number
  scheduler_running: boolean
  active_sessions: number[]
}

// ==========================================================================
// CloudWatch Alarm Types
// ==========================================================================

type CwAlarmSessionSummary = {
  id: number
  status: string
  trigger_type: string
  environment: string
  total_alarms: number
  fix_created: number
  not_an_issue: number
  escalated: number
  skipped: number
  failed: number
  started_by: string
  error_message: string | null
  created_at: string
  completed_at: string | null
}

type CwAlarmItemDetail = {
  id: number
  alarm_arn: string
  alarm_name: string
  alarm_state: string | null
  alarm_reason: string | null
  metric_namespace: string | null
  metric_name: string | null
  dimensions: string | null
  step: string
  status: string
  result: string
  repo_name: string | null
  pr_url: string | null
  pr_number: number | null
  pr_branch: string | null
  twp_task_id: string | null
  twp_task_key: string | null
  investigation_summary: string | null
  escalation_detail: string | null
  error_message: string | null
  created_at: string
  updated_at: string
}

type CwAlarmSessionDetail = CwAlarmSessionSummary & {
  items: CwAlarmItemDetail[]
}

type CwAlarmTrackerEntry = {
  alarm_arn: string
  alarm_name: string | null
  latest_session_id: number
  latest_status: string
  pr_url: string | null
  twp_task_id: string | null
  twp_task_key: string | null
  resolved_at: string | null
  created_at: string
  updated_at: string
}

type CwAlarmSchedulerStatus = {
  auto_enabled: boolean
  interval_seconds: number
  target_environment: string
  scheduler_running: boolean
  active_sessions: number[]
}

// ==========================================================================
// Sentry Constants & Helpers
// ==========================================================================

const SENTRY_ISSUE_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: '待機', cls: 'bg-gray-500/20 text-gray-400' },
  running: { label: '処理中', cls: 'bg-blue-500/20 text-blue-400' },
  pr_created: { label: 'PR作成完了', cls: 'bg-green-500/20 text-green-400' },
  not_a_bug: { label: '対応不要', cls: 'bg-gray-500/20 text-gray-400' },
  escalation: { label: 'エスカレーション', cls: 'bg-orange-500/20 text-orange-400' },
  duplicate: { label: '重複', cls: 'bg-cyan-500/20 text-cyan-400' },
  failed: { label: '失敗', cls: 'bg-red-500/20 text-red-400' },
  skipped: { label: 'スキップ', cls: 'bg-yellow-500/20 text-yellow-400' },
}

const SENTRY_TRACKER_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pr_created: { label: 'PR作成', cls: 'bg-green-500/20 text-green-400' },
  not_a_bug: { label: '対応不要', cls: 'bg-gray-500/20 text-gray-400' },
  escalation: { label: 'エスカレーション', cls: 'bg-orange-500/20 text-orange-400' },
  duplicate: { label: '重複', cls: 'bg-cyan-500/20 text-cyan-400' },
  investigating: { label: '調査中', cls: 'bg-blue-500/20 text-blue-400' },
  failed: { label: '失敗', cls: 'bg-red-500/20 text-red-400' },
}

function SentryIssueStatusBadge({ status }: { status: string }) {
  const s = SENTRY_ISSUE_STATUS_BADGE[status] ?? SENTRY_ISSUE_STATUS_BADGE.pending
  return <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', s.cls)}>{s.label}</span>
}

function LevelBadge({ level }: { level: string | null }) {
  if (!level) return null
  const colors: Record<string, string> = {
    error: 'bg-red-500/20 text-red-400',
    warning: 'bg-yellow-500/20 text-yellow-400',
    info: 'bg-blue-500/20 text-blue-400',
    fatal: 'bg-red-600/30 text-red-300',
  }
  return (
    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', colors[level] ?? 'bg-gray-500/20 text-gray-400')}>
      {level}
    </span>
  )
}

function getSentryStepStatus(issue: SentryIssueDetail, step: string): string {
  const currentIdx = PIPELINE_STEPS.indexOf(issue.step as typeof PIPELINE_STEPS[number])
  const stepIdx = PIPELINE_STEPS.indexOf(step as typeof PIPELINE_STEPS[number])

  if (step === issue.step) {
    if (issue.status === 'running') return 'running'
    if (issue.status === 'pr_created' && step === 'done') return 'pr_created'
    if (issue.status === 'not_a_bug' && step === 'done') return 'not_a_bug'
    if (issue.status === 'escalation' && step === 'done') return 'escalation'
    if (issue.status === 'duplicate' && step === 'done') return 'duplicate'
    if (issue.status === 'failed') return 'failed'
    return issue.status
  }

  if (stepIdx < currentIdx) return 'success'
  return 'pending'
}

// ==========================================================================
// CloudWatch Alarm Constants & Helpers
// ==========================================================================

const CW_ALARM_ITEM_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  pending: { label: '待機', cls: 'bg-gray-500/20 text-gray-400' },
  running: { label: '処理中', cls: 'bg-blue-500/20 text-blue-400' },
  fix_created: { label: 'Fix作成完了', cls: 'bg-green-500/20 text-green-400' },
  not_an_issue: { label: '対応不要', cls: 'bg-gray-500/20 text-gray-400' },
  escalation: { label: 'エスカレーション', cls: 'bg-orange-500/20 text-orange-400' },
  failed: { label: '失敗', cls: 'bg-red-500/20 text-red-400' },
  skipped: { label: 'スキップ', cls: 'bg-yellow-500/20 text-yellow-400' },
}

const CW_ALARM_TRACKER_STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  fix_created: { label: 'Fix作成', cls: 'bg-green-500/20 text-green-400' },
  not_an_issue: { label: '対応不要', cls: 'bg-gray-500/20 text-gray-400' },
  escalation: { label: 'エスカレーション', cls: 'bg-orange-500/20 text-orange-400' },
  investigating: { label: '調査中', cls: 'bg-blue-500/20 text-blue-400' },
  failed: { label: '失敗', cls: 'bg-red-500/20 text-red-400' },
}

function CwAlarmItemStatusBadge({ status }: { status: string }) {
  const s = CW_ALARM_ITEM_STATUS_BADGE[status] ?? CW_ALARM_ITEM_STATUS_BADGE.pending
  return <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', s.cls)}>{s.label}</span>
}

function getCwAlarmStepStatus(item: CwAlarmItemDetail, step: string): string {
  const currentIdx = PIPELINE_STEPS.indexOf(item.step as typeof PIPELINE_STEPS[number])
  const stepIdx = PIPELINE_STEPS.indexOf(step as typeof PIPELINE_STEPS[number])

  if (step === item.step) {
    if (item.status === 'running') return 'running'
    if (item.status === 'fix_created' && step === 'done') return 'fix_created'
    if (item.status === 'not_an_issue' && step === 'done') return 'not_an_issue'
    if (item.status === 'escalation' && step === 'done') return 'escalation'
    if (item.status === 'failed') return 'failed'
    return item.status
  }

  if (stepIdx < currentIdx) return 'success'
  return 'pending'
}

// ==========================================================================
// Sentry Sessions Tab
// ==========================================================================

function SentrySessionsTab() {
  const [sessions, setSessions] = useState<SentrySessionSummary[]>([])
  const [currentSession, setCurrentSession] = useState<SentrySessionDetail | null>(null)
  const [streamContent, setStreamContent] = useState('')
  const [expandedIssue, setExpandedIssue] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [scheduler, setScheduler] = useState<SchedulerStatus | null>(null)
  const socketRef = useRef<ReturnType<typeof createBugFixSocket> | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const sessionIdRef = useRef<number | null>(null)

  const loadSessions = useCallback(async () => {
    try {
      const data = await fetchApi<SentrySessionSummary[]>('/bugfix/sessions')
      setSessions(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load sessions')
    }
  }, [])

  const loadScheduler = useCallback(async () => {
    try {
      const data = await fetchApi<SchedulerStatus>('/bugfix/scheduler')
      setScheduler(data)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    loadSessions()
    loadScheduler()
  }, [loadSessions, loadScheduler])

  const startPolling = useCallback((sessionId: number) => {
    sessionIdRef.current = sessionId
    if (pollingRef.current) clearInterval(pollingRef.current)

    const poll = async () => {
      if (!sessionIdRef.current) return
      try {
        const detail = await fetchApi<SentrySessionDetail>(`/bugfix/sessions/${sessionIdRef.current}`)
        setCurrentSession(detail)
        if (['completed', 'failed', 'cancelled', 'interrupted'].includes(detail.status)) {
          setLoading(false)
          stopPolling()
          loadSessions()
        }
      } catch { /* ignore */ }
    }

    pollingRef.current = setInterval(poll, 3000)
    poll()
  }, [loadSessions])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    sessionIdRef.current = null
  }, [])

  const connectWs = useCallback(() => {
    if (socketRef.current) return

    const socket = createBugFixSocket(
      (event: BugFixEvent) => {
        if (event.type === 'bugfix_session_status') {
          if (['completed', 'failed', 'cancelled', 'interrupted'].includes(event.status ?? '')) {
            setLoading(false)
            stopPolling()
            if (sessionIdRef.current) {
              fetchApi<SentrySessionDetail>(`/bugfix/sessions/${sessionIdRef.current}`)
                .then((detail) => setCurrentSession(detail))
                .catch(() => {})
            }
            loadSessions()
          }
        } else if (event.type === 'bugfix_issue_update') {
          setCurrentSession((prev) => {
            if (!prev) return prev
            const issues = prev.issues.map((iss) =>
              iss.id === event.issue_id
                ? { ...iss, ...Object.fromEntries(
                    Object.entries(event).filter(([k]) => k !== 'type' && k !== 'session_id')
                  )}
                : iss,
            )
            return { ...prev, issues }
          })
        } else if (event.type === 'bugfix_stream') {
          if (event.content) {
            setStreamContent((prev) => prev + event.content!)
          } else if (event.tool_name && event.tool_input) {
            // Add visual separator between AI comments when tool calls happen
            setStreamContent((prev) => prev ? prev + '\n\n---\n\n' : prev)
          }
        }
      },
      () => { socketRef.current = null },
    )
    socketRef.current = socket
  }, [stopPolling, loadSessions])

  useEffect(() => {
    return () => {
      socketRef.current?.close()
      stopPolling()
    }
  }, [stopPolling])

  const startBugFix = async () => {
    setError('')
    setLoading(true)
    setStreamContent('')
    setExpandedIssue(null)

    try {
      const result = await fetchApi<{ status: string; session_id?: number; message?: string }>(
        '/bugfix/start',
        { method: 'POST' },
      )

      if (result.status === 'error') {
        setError(result.message ?? 'Unknown error')
        setLoading(false)
        return
      }

      if (result.session_id) {
        const detail = await fetchApi<SentrySessionDetail>(`/bugfix/sessions/${result.session_id}`)
        setCurrentSession(detail)
        startPolling(result.session_id)
        connectWs()
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start bug fix')
      setLoading(false)
    }
  }

  const cancelSession = async () => {
    if (!currentSession) return
    try {
      const result = await fetchApi<{ status: string; message?: string }>(
        `/bugfix/sessions/${currentSession.id}/cancel`,
        { method: 'POST' },
      )
      if (result.status === 'error') {
        setError(result.message ?? 'Failed to cancel session')
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to cancel session')
    }
  }

  const loadSession = async (id: number) => {
    try {
      const detail = await fetchApi<SentrySessionDetail>(`/bugfix/sessions/${id}`)
      setCurrentSession(detail)
      setStreamContent('')
      setExpandedIssue(null)

      if (detail.status === 'running') {
        startPolling(detail.id)
        connectWs()
        setLoading(true)
      } else {
        setLoading(false)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load session')
    }
  }

  const isRunning = currentSession?.status === 'running' || loading

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 shrink-0 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {currentSession && <SessionStatusBadge status={currentSession.status} />}
          {scheduler && (
            <span className={cn(
              'px-2 py-0.5 rounded text-[10px] font-medium',
              scheduler.auto_enabled
                ? 'bg-green-500/20 text-green-400'
                : 'bg-gray-500/20 text-gray-400',
            )}>
              Scheduler: {scheduler.auto_enabled ? 'ON' : 'OFF'}
              {scheduler.auto_enabled && ` (${Math.round(scheduler.interval_seconds / 60)}min)`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRunning && currentSession && (
            <button
              onClick={cancelSession}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
            >
              <XCircle size={14} />
              Cancel
            </button>
          )}
          <button
            onClick={startBugFix}
            disabled={isRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] hover:opacity-90 text-white transition-colors disabled:opacity-40"
          >
            <Play size={14} />
            Start
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm shrink-0">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Counter bar */}
      {currentSession && (
        <div className="mb-3 flex items-center gap-4 text-sm shrink-0 flex-wrap">
          <span className="text-[var(--color-text-muted)]">Total: <strong>{currentSession.total_issues}</strong></span>
          <span className="text-green-400">PR: <strong>{currentSession.pr_created}</strong></span>
          <span className="text-gray-400">不要: <strong>{currentSession.not_a_bug}</strong></span>
          <span className="text-orange-400">エスカ: <strong>{currentSession.escalated}</strong></span>
          {(currentSession.duplicate ?? 0) > 0 && <span className="text-cyan-400">重複: <strong>{currentSession.duplicate}</strong></span>}
          <span className="text-red-400">失敗: <strong>{currentSession.failed}</strong></span>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {/* Current session issues */}
        {currentSession && currentSession.issues.length > 0 && (
          <div className="space-y-2 mb-4">
            {currentSession.issues.map((issue) => {
              const isExpanded = expandedIssue === issue.id
              return (
                <div key={issue.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
                  <button
                    onClick={() => setExpandedIssue(isExpanded ? null : issue.id)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--color-bg-hover)] transition-colors text-left"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <LevelBadge level={issue.sentry_level} />
                    <span className="font-mono text-xs text-[var(--color-text-muted)]">{issue.sentry_project}</span>
                    <span className="text-xs truncate flex-1" title={issue.sentry_title ?? ''}>
                      {issue.sentry_title ?? ''}
                    </span>
                    <SentryIssueStatusBadge status={issue.status} />
                  </button>

                  {/* Pipeline steps */}
                  <div className="px-4 pb-2 flex items-center gap-1">
                    {PIPELINE_STEPS.map((step, idx) => {
                      const stepStatus = getSentryStepStatus(issue, step)
                      return (
                        <div key={step} className="flex items-center gap-1">
                          {idx > 0 && <ArrowRight size={10} className="text-[var(--color-text-muted)]/30 mx-0.5" />}
                          <div className="flex items-center gap-1" title={`${STEP_LABELS[step]}: ${stepStatus}`}>
                            <StepIcon status={stepStatus} />
                            <span className="text-[10px] text-[var(--color-text-muted)]">{STEP_LABELS[step]}</span>
                          </div>
                        </div>
                      )
                    })}

                    {/* Quick links */}
                    <div className="ml-auto flex items-center gap-2">
                      {issue.pr_url && (
                        <a href={issue.pr_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[var(--color-primary)] hover:underline flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                          <GitPullRequest size={10} />
                          PR#{issue.pr_number}
                        </a>
                      )}
                      {issue.twp_task_key && (
                        <span className="text-[10px] text-[var(--color-text-muted)] flex items-center gap-0.5">
                          <ClipboardList size={10} />
                          {issue.twp_task_key}
                        </span>
                      )}
                      {issue.sentry_permalink && (
                        <a href={issue.sentry_permalink} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" onClick={(e) => e.stopPropagation()}>
                          <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-3 border-t border-[var(--color-border)] pt-2 space-y-2">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] text-[var(--color-text-muted)]">
                        <div>Culprit: <span className="font-mono text-[var(--color-text)]">{issue.sentry_culprit ?? 'N/A'}</span></div>
                        <div>Count: <span className="text-[var(--color-text)]">{issue.sentry_count ?? 'N/A'}</span></div>
                        <div>First: <span className="text-[var(--color-text)]">{formatDate(issue.sentry_first_seen) || 'N/A'}</span></div>
                        <div>Last: <span className="text-[var(--color-text)]">{formatDate(issue.sentry_last_seen) || 'N/A'}</span></div>
                      </div>

                      {issue.result && (
                        <div className="text-xs text-[var(--color-text)]">Result: {issue.result}</div>
                      )}

                      {issue.investigation_summary && (
                        <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                          <span className="text-[10px] text-[var(--color-text-muted)] font-medium">AI Investigation:</span>
                          <div className="mt-1 max-h-48 overflow-auto">
                            <MarkdownRenderer content={issue.investigation_summary} className="text-xs" />
                          </div>
                        </div>
                      )}

                      {issue.escalation_detail && !issue.investigation_summary && (
                        <div className="p-2 rounded bg-orange-500/5 border border-orange-500/20">
                          <span className="text-[10px] text-orange-400 font-medium">Escalation Detail:</span>
                          <p className="mt-1 text-xs">{issue.escalation_detail}</p>
                        </div>
                      )}

                      {issue.error_message && (
                        <div className="p-2 rounded bg-red-500/5 border border-red-500/20">
                          <span className="text-[10px] text-red-400 font-medium">Error:</span>
                          <p className="mt-1 text-xs font-mono">{issue.error_message}</p>
                        </div>
                      )}

                      {issue.repo_name && (
                        <div className="text-[10px] text-[var(--color-text-muted)]">
                          Repo: <span className="font-mono">{issue.repo_name}</span>
                          {issue.pr_branch && <> &middot; Branch: <span className="font-mono">{issue.pr_branch}</span></>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* AI Activity stream */}
        {isRunning && streamContent && (
          <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
            <div className="px-4 py-2 border-b border-[var(--color-border)] flex items-center gap-2 text-sm">
              <Loader2 size={14} className="text-blue-400 animate-spin" />
              <span className="font-medium">AI Activity</span>
            </div>
            <div className="p-4 max-h-64 overflow-auto">
              <MarkdownRenderer content={streamContent} isStreaming={true} className="text-xs" />
            </div>
          </div>
        )}

        {/* Session history */}
        {!currentSession && sessions.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-[var(--color-text-muted)] mb-2">Session History</h3>
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => loadSession(s.id)}
                className="w-full text-left p-3 rounded-lg bg-[var(--color-bg-card)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono">#{s.id}</span>
                    <SessionStatusBadge status={s.status} />
                    <span className={cn(
                      'px-1.5 py-0.5 rounded text-[10px] font-medium',
                      s.trigger_type === 'auto' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400',
                    )}>
                      {s.trigger_type}
                    </span>
                  </div>
                  <span className="text-xs text-[var(--color-text-muted)]">{formatDateTime(s.created_at)}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-[var(--color-text-muted)]">
                  <span>{s.started_by}</span>
                  <span>{s.total_issues} issues</span>
                  {s.pr_created > 0 && <span className="text-green-400">{s.pr_created} PRs</span>}
                  {s.escalated > 0 && <span className="text-orange-400">{s.escalated} escalated</span>}
                  {s.not_a_bug > 0 && <span className="text-gray-400">{s.not_a_bug} not bugs</span>}
                  {(s.duplicate ?? 0) > 0 && <span className="text-cyan-400">{s.duplicate} duplicates</span>}
                  {s.failed > 0 && <span className="text-red-400">{s.failed} failed</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!currentSession && sessions.length === 0 && (
          <div className="text-center py-16 text-[var(--color-text-muted)]">
            <ShieldAlert size={48} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">Sentry Bug Fix Pipeline</p>
            <p className="text-xs mt-1">
              Sentryのアクティブなエラーを自動検出し、AIが調査・修正・PR作成まで行います
            </p>
          </div>
        )}
      </div>
    </>
  )
}

// ==========================================================================
// Sentry Tracker Tab
// ==========================================================================

function SentryTrackerTab() {
  const [entries, setEntries] = useState<SentryTrackerEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    fetchApi<SentryTrackerEntry[]>('/bugfix/tracker')
      .then(setEntries)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load tracker'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-[var(--color-text-muted)]" />
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {entries.length > 0 ? (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Sentry ID</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Project</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Title</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Status</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">PR / Task</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Updated</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const badge = SENTRY_TRACKER_STATUS_BADGE[e.latest_status] ?? SENTRY_TRACKER_STATUS_BADGE.failed
                return (
                  <tr key={e.sentry_issue_id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="px-3 py-2 text-xs font-mono">{e.sentry_issue_id}</td>
                    <td className="px-3 py-2 text-xs font-mono">{e.sentry_project ?? '-'}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[200px]" title={e.sentry_title ?? ''}>
                      {e.sentry_title ?? '-'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', badge.cls)}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.pr_url && (
                        <a href={e.pr_url} target="_blank" rel="noopener noreferrer" className="text-[var(--color-primary)] hover:underline flex items-center gap-0.5">
                          <GitPullRequest size={10} />
                          PR
                        </a>
                      )}
                      {e.twp_task_key && (
                        <span className="text-[var(--color-text-muted)]">{e.twp_task_key}</span>
                      )}
                      {!e.pr_url && !e.twp_task_key && '-'}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-[var(--color-text-muted)]">
                      {formatDateTime(e.updated_at)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-16 text-[var(--color-text-muted)]">
          <Search size={48} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">No tracked issues yet</p>
          <p className="text-xs mt-1">Start a Bug Fix session to begin tracking Sentry issues</p>
        </div>
      )}
    </>
  )
}

// ==========================================================================
// CloudWatch Alarm Sessions Tab
// ==========================================================================

function CwAlarmSessionsTab() {
  const [sessions, setSessions] = useState<CwAlarmSessionSummary[]>([])
  const [currentSession, setCurrentSession] = useState<CwAlarmSessionDetail | null>(null)
  const [streamContent, setStreamContent] = useState('')
  const [expandedItem, setExpandedItem] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [scheduler, setScheduler] = useState<CwAlarmSchedulerStatus | null>(null)
  const socketRef = useRef<ReturnType<typeof createCwAlarmSocket> | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const sessionIdRef = useRef<number | null>(null)

  const loadSessions = useCallback(async () => {
    try {
      const data = await fetchApi<CwAlarmSessionSummary[]>('/cw-alarm/sessions')
      setSessions(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load sessions')
    }
  }, [])

  const loadScheduler = useCallback(async () => {
    try {
      const data = await fetchApi<CwAlarmSchedulerStatus>('/cw-alarm/scheduler')
      setScheduler(data)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    loadSessions()
    loadScheduler()
  }, [loadSessions, loadScheduler])

  const startPolling = useCallback((sessionId: number) => {
    sessionIdRef.current = sessionId
    if (pollingRef.current) clearInterval(pollingRef.current)

    const poll = async () => {
      if (!sessionIdRef.current) return
      try {
        const detail = await fetchApi<CwAlarmSessionDetail>(`/cw-alarm/sessions/${sessionIdRef.current}`)
        setCurrentSession(detail)
        if (['completed', 'failed', 'cancelled', 'interrupted'].includes(detail.status)) {
          setLoading(false)
          stopPolling()
          loadSessions()
        }
      } catch { /* ignore */ }
    }

    pollingRef.current = setInterval(poll, 3000)
    poll()
  }, [loadSessions])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    sessionIdRef.current = null
  }, [])

  const connectWs = useCallback(() => {
    if (socketRef.current) return

    const socket = createCwAlarmSocket(
      (event: CwAlarmEvent) => {
        if (event.type === 'cw_alarm_session_status') {
          if (['completed', 'failed', 'cancelled', 'interrupted'].includes(event.status ?? '')) {
            setLoading(false)
            stopPolling()
            if (sessionIdRef.current) {
              fetchApi<CwAlarmSessionDetail>(`/cw-alarm/sessions/${sessionIdRef.current}`)
                .then((detail) => setCurrentSession(detail))
                .catch(() => {})
            }
            loadSessions()
          }
        } else if (event.type === 'cw_alarm_item_update') {
          setCurrentSession((prev) => {
            if (!prev) return prev
            const items = prev.items.map((item) =>
              item.id === event.item_id
                ? { ...item, ...Object.fromEntries(
                    Object.entries(event).filter(([k]) => k !== 'type' && k !== 'session_id')
                  )}
                : item,
            )
            return { ...prev, items }
          })
        } else if (event.type === 'cw_alarm_stream') {
          if (event.content) {
            setStreamContent((prev) => prev + event.content!)
          }
        }
      },
      () => { socketRef.current = null },
    )
    socketRef.current = socket
  }, [stopPolling, loadSessions])

  useEffect(() => {
    return () => {
      socketRef.current?.close()
      stopPolling()
    }
  }, [stopPolling])

  const startAlarmCheck = async () => {
    setError('')
    setLoading(true)
    setStreamContent('')
    setExpandedItem(null)

    try {
      const result = await fetchApi<{ status: string; session_id?: number; message?: string }>(
        '/cw-alarm/start',
        { method: 'POST' },
      )

      if (result.status === 'error') {
        setError(result.message ?? 'Unknown error')
        setLoading(false)
        return
      }

      if (result.session_id) {
        const detail = await fetchApi<CwAlarmSessionDetail>(`/cw-alarm/sessions/${result.session_id}`)
        setCurrentSession(detail)
        startPolling(result.session_id)
        connectWs()
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start alarm check')
      setLoading(false)
    }
  }

  const cancelSession = async () => {
    if (!currentSession) return
    try {
      const result = await fetchApi<{ status: string; message?: string }>(
        `/cw-alarm/sessions/${currentSession.id}/cancel`,
        { method: 'POST' },
      )
      if (result.status === 'error') {
        setError(result.message ?? 'Failed to cancel session')
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to cancel session')
    }
  }

  const loadSession = async (id: number) => {
    try {
      const detail = await fetchApi<CwAlarmSessionDetail>(`/cw-alarm/sessions/${id}`)
      setCurrentSession(detail)
      setStreamContent('')
      setExpandedItem(null)

      if (detail.status === 'running') {
        startPolling(detail.id)
        connectWs()
        setLoading(true)
      } else {
        setLoading(false)
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load session')
    }
  }

  const isRunning = currentSession?.status === 'running' || loading

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 shrink-0 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {currentSession && <SessionStatusBadge status={currentSession.status} />}
          {scheduler && (
            <>
              <span className={cn(
                'px-2 py-0.5 rounded text-[10px] font-medium',
                scheduler.auto_enabled
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-gray-500/20 text-gray-400',
              )}>
                Scheduler: {scheduler.auto_enabled ? 'ON' : 'OFF'}
                {scheduler.auto_enabled && ` (${Math.round(scheduler.interval_seconds / 60)}min)`}
              </span>
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400">
                {scheduler.target_environment}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRunning && currentSession && (
            <button
              onClick={cancelSession}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
            >
              <XCircle size={14} />
              Cancel
            </button>
          )}
          <button
            onClick={startAlarmCheck}
            disabled={isRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded bg-[var(--color-primary)] hover:opacity-90 text-white transition-colors disabled:opacity-40"
          >
            <Play size={14} />
            Start
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm shrink-0">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline text-xs">dismiss</button>
        </div>
      )}

      {/* Counter bar */}
      {currentSession && (
        <div className="mb-3 flex items-center gap-4 text-sm shrink-0 flex-wrap">
          <span className="text-[var(--color-text-muted)]">Total: <strong>{currentSession.total_alarms}</strong></span>
          <span className="text-green-400">Fix: <strong>{currentSession.fix_created}</strong></span>
          <span className="text-gray-400">不要: <strong>{currentSession.not_an_issue}</strong></span>
          <span className="text-orange-400">エスカ: <strong>{currentSession.escalated}</strong></span>
          <span className="text-red-400">失敗: <strong>{currentSession.failed}</strong></span>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto">
        {/* Current session items */}
        {currentSession && currentSession.items.length > 0 && (
          <div className="space-y-2 mb-4">
            {currentSession.items.map((item) => {
              const isExpanded = expandedItem === item.id
              return (
                <div key={item.id} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
                  <button
                    onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[var(--color-bg-hover)] transition-colors text-left"
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400">
                      {item.alarm_state ?? 'ALARM'}
                    </span>
                    <span className="font-mono text-xs text-[var(--color-text-muted)]">{item.metric_namespace}</span>
                    <span className="text-xs truncate flex-1" title={item.alarm_name}>
                      {item.alarm_name}
                    </span>
                    <CwAlarmItemStatusBadge status={item.status} />
                  </button>

                  {/* Pipeline steps */}
                  <div className="px-4 pb-2 flex items-center gap-1">
                    {PIPELINE_STEPS.map((step, idx) => {
                      const stepStatus = getCwAlarmStepStatus(item, step)
                      return (
                        <div key={step} className="flex items-center gap-1">
                          {idx > 0 && <ArrowRight size={10} className="text-[var(--color-text-muted)]/30 mx-0.5" />}
                          <div className="flex items-center gap-1" title={`${STEP_LABELS[step]}: ${stepStatus}`}>
                            <StepIcon status={stepStatus} />
                            <span className="text-[10px] text-[var(--color-text-muted)]">{STEP_LABELS[step]}</span>
                          </div>
                        </div>
                      )
                    })}

                    {/* Quick links */}
                    <div className="ml-auto flex items-center gap-2">
                      {item.pr_url && (
                        <a href={item.pr_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[var(--color-primary)] hover:underline flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                          <GitPullRequest size={10} />
                          PR#{item.pr_number}
                        </a>
                      )}
                      {item.twp_task_key && (
                        <span className="text-[10px] text-[var(--color-text-muted)] flex items-center gap-0.5">
                          <ClipboardList size={10} />
                          {item.twp_task_key}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-3 border-t border-[var(--color-border)] pt-2 space-y-2">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[10px] text-[var(--color-text-muted)]">
                        <div>Metric: <span className="font-mono text-[var(--color-text)]">{item.metric_name ?? 'N/A'}</span></div>
                        <div>Namespace: <span className="font-mono text-[var(--color-text)]">{item.metric_namespace ?? 'N/A'}</span></div>
                        <div>Dimensions: <span className="font-mono text-[var(--color-text)]">{item.dimensions ?? 'N/A'}</span></div>
                      </div>

                      {item.alarm_reason && (
                        <div className="text-xs text-[var(--color-text-muted)]">
                          Reason: <span className="text-[var(--color-text)]">{item.alarm_reason}</span>
                        </div>
                      )}

                      {item.result && (
                        <div className="text-xs text-[var(--color-text)]">Result: {item.result}</div>
                      )}

                      {item.investigation_summary && (
                        <div className="p-2 rounded bg-[var(--color-bg)] border border-[var(--color-border)]">
                          <span className="text-[10px] text-[var(--color-text-muted)] font-medium">AI Investigation:</span>
                          <div className="mt-1 max-h-48 overflow-auto">
                            <MarkdownRenderer content={item.investigation_summary} className="text-xs" />
                          </div>
                        </div>
                      )}

                      {item.escalation_detail && !item.investigation_summary && (
                        <div className="p-2 rounded bg-orange-500/5 border border-orange-500/20">
                          <span className="text-[10px] text-orange-400 font-medium">Escalation Detail:</span>
                          <p className="mt-1 text-xs">{item.escalation_detail}</p>
                        </div>
                      )}

                      {item.error_message && (
                        <div className="p-2 rounded bg-red-500/5 border border-red-500/20">
                          <span className="text-[10px] text-red-400 font-medium">Error:</span>
                          <p className="mt-1 text-xs font-mono">{item.error_message}</p>
                        </div>
                      )}

                      {item.repo_name && (
                        <div className="text-[10px] text-[var(--color-text-muted)]">
                          Repo: <span className="font-mono">{item.repo_name}</span>
                          {item.pr_branch && <> &middot; Branch: <span className="font-mono">{item.pr_branch}</span></>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* AI Activity stream */}
        {isRunning && streamContent && (
          <div className="mb-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] overflow-hidden">
            <div className="px-4 py-2 border-b border-[var(--color-border)] flex items-center gap-2 text-sm">
              <Loader2 size={14} className="text-blue-400 animate-spin" />
              <span className="font-medium">AI Activity</span>
            </div>
            <div className="p-4 max-h-64 overflow-auto">
              <MarkdownRenderer content={streamContent} isStreaming={true} className="text-xs" />
            </div>
          </div>
        )}

        {/* Session history */}
        {!currentSession && sessions.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-[var(--color-text-muted)] mb-2">Session History</h3>
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => loadSession(s.id)}
                className="w-full text-left p-3 rounded-lg bg-[var(--color-bg-card)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono">#{s.id}</span>
                    <SessionStatusBadge status={s.status} />
                    <span className={cn(
                      'px-1.5 py-0.5 rounded text-[10px] font-medium',
                      s.trigger_type === 'auto' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400',
                    )}>
                      {s.trigger_type}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400">
                      {s.environment}
                    </span>
                  </div>
                  <span className="text-xs text-[var(--color-text-muted)]">{formatDateTime(s.created_at)}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-[var(--color-text-muted)]">
                  <span>{s.started_by}</span>
                  <span>{s.total_alarms} alarms</span>
                  {s.fix_created > 0 && <span className="text-green-400">{s.fix_created} fixes</span>}
                  {s.escalated > 0 && <span className="text-orange-400">{s.escalated} escalated</span>}
                  {s.not_an_issue > 0 && <span className="text-gray-400">{s.not_an_issue} not issues</span>}
                  {s.failed > 0 && <span className="text-red-400">{s.failed} failed</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!currentSession && sessions.length === 0 && (
          <div className="text-center py-16 text-[var(--color-text-muted)]">
            <Bell size={48} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium">CloudWatch Alarm Monitor</p>
            <p className="text-xs mt-1">
              CloudWatchのアクティブなアラームを自動検出し、AIが調査・修正・エスカレーションを行います
            </p>
          </div>
        )}
      </div>
    </>
  )
}

// ==========================================================================
// CloudWatch Alarm Tracker Tab
// ==========================================================================

function CwAlarmTrackerTab() {
  const [entries, setEntries] = useState<CwAlarmTrackerEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    fetchApi<CwAlarmTrackerEntry[]>('/cw-alarm/tracker')
      .then(setEntries)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load tracker'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-[var(--color-text-muted)]" />
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="mb-3 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}

      {entries.length > 0 ? (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Alarm Name</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Status</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Resolved</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">PR / Task</th>
                <th className="text-left px-3 py-2 text-[var(--color-text-muted)] font-medium text-xs">Updated</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const badge = CW_ALARM_TRACKER_STATUS_BADGE[e.latest_status] ?? CW_ALARM_TRACKER_STATUS_BADGE.failed
                return (
                  <tr key={e.alarm_arn} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="px-3 py-2 text-xs font-mono truncate max-w-[250px]" title={e.alarm_name ?? e.alarm_arn}>
                      {e.alarm_name ?? e.alarm_arn}
                    </td>
                    <td className="px-3 py-2">
                      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', badge.cls)}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.resolved_at ? (
                        <span className="text-green-400">OK</span>
                      ) : (
                        <span className="text-red-400">ALARM</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.pr_url && (
                        <a href={e.pr_url} target="_blank" rel="noopener noreferrer" className="text-[var(--color-primary)] hover:underline flex items-center gap-0.5">
                          <GitPullRequest size={10} />
                          PR
                        </a>
                      )}
                      {e.twp_task_key && (
                        <span className="text-[var(--color-text-muted)]">{e.twp_task_key}</span>
                      )}
                      {!e.pr_url && !e.twp_task_key && '-'}
                    </td>
                    <td className="px-3 py-2 text-[10px] text-[var(--color-text-muted)]">
                      {formatDateTime(e.updated_at)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-16 text-[var(--color-text-muted)]">
          <Search size={48} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">No tracked alarms yet</p>
          <p className="text-xs mt-1">Start an Alarm Check session to begin tracking CloudWatch alarms</p>
        </div>
      )}
    </>
  )
}

// ==========================================================================
// Main Component with 2-level tabs
// ==========================================================================

export function BugFix() {
  const [sourceTab, setSourceTab] = useState<'sentry' | 'cloudwatch'>('sentry')
  const [subTab, setSubTab] = useState<'sessions' | 'tracker'>('sessions')

  return (
    <div className="p-4 h-full flex flex-col overflow-hidden">
      {/* Page header with 2-level tabs */}
      <div className="flex items-center gap-4 mb-4 shrink-0">
        <ShieldAlert size={22} className="text-[var(--color-primary)]" />

        {/* Source tabs (Sentry / CloudWatch) */}
        <div className="flex items-center gap-1 bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-0.5">
          <button
            onClick={() => { setSourceTab('sentry'); setSubTab('sessions') }}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md transition-colors',
              sourceTab === 'sentry'
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
            )}
          >
            <span className="flex items-center gap-1.5">
              <ShieldAlert size={14} />
              Sentry
            </span>
          </button>
          <button
            onClick={() => { setSourceTab('cloudwatch'); setSubTab('sessions') }}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md transition-colors',
              sourceTab === 'cloudwatch'
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
            )}
          >
            <span className="flex items-center gap-1.5">
              <Bell size={14} />
              CloudWatch
            </span>
          </button>
        </div>

        {/* Sub tabs (Sessions / Tracker) */}
        <div className="flex items-center gap-1 bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-0.5">
          <button
            onClick={() => setSubTab('sessions')}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md transition-colors',
              subTab === 'sessions'
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
            )}
          >
            <span className="flex items-center gap-1.5">
              <History size={14} />
              Sessions
            </span>
          </button>
          <button
            onClick={() => setSubTab('tracker')}
            className={cn(
              'px-3 py-1.5 text-xs rounded-md transition-colors',
              subTab === 'tracker'
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
            )}
          >
            <span className="flex items-center gap-1.5">
              <Search size={14} />
              Tracker
            </span>
          </button>
        </div>
      </div>

      {/* Tab content */}
      {sourceTab === 'sentry' && subTab === 'sessions' && <SentrySessionsTab />}
      {sourceTab === 'sentry' && subTab === 'tracker' && <SentryTrackerTab />}
      {sourceTab === 'cloudwatch' && subTab === 'sessions' && <CwAlarmSessionsTab />}
      {sourceTab === 'cloudwatch' && subTab === 'tracker' && <CwAlarmTrackerTab />}
    </div>
  )
}
